import { ProgressOperationConfig, ProgressStep } from 'src/app/_shared/components/progress/models/progress-definitions';
import { CodesReports } from 'src/environments/codes-reports';

export const REQUETEUR_RECHERCHE = 'REQUETEUR_RECHERCHE';
export const REQUETEUR_EXPORT = 'REQUETEUR_EXPORT';
export const REQUETEUR_EXPORT_LARGE = 'REQUETEUR_EXPORT_LARGE';
export const ATTRIBUTION_PREPARATION = 'ATTRIBUTION_PREPARATION';

// ============================================================================
// ÉTAPES COMMUNES RÉUTILISABLES
// ============================================================================

const STEP_DATA: ProgressStep = {
    id: 'data',
    label: 'Récupération des données',
    weight: 55,                      // Augmenté : prend la majorité du temps réel
    estimatedDurationMs: 12000       // Augmenté pour éviter l'effet "bloqué"
};

const STEP_PDF: ProgressStep = {
    id: 'pdf',
    label: 'Génération du document',
    weight: 15,                      // Réduit : se termine très vite en réalité
    estimatedDurationMs: 2000        // Réduit pour refléter la réalité
};

const STEP_RENDER: ProgressStep = {
    id: 'render',
    label: 'Préparation de l\'aperçu',
    weight: 30,
    estimatedDurationMs: 5000,
    hasRealProgress: true
};

const STEP_EXPORT_PREPARE: ProgressStep = {
    id: 'prepare',
    label: 'Préparation des données',
    weight: 30,
    estimatedDurationMs: 5000
};

const STEP_EXPORT_GENERATE: ProgressStep = {
    id: 'generate',
    label: 'Génération du fichier',
    weight: 50,
    estimatedDurationMs: 10000,
    hasRealProgress: true
};

const STEP_EXPORT_DOWNLOAD: ProgressStep = {
    id: 'download',
    label: 'Téléchargement',
    weight: 20,
    estimatedDurationMs: 2000
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Crée une config de rapport standard (data → pdf → render)
 */
function buildReportConfig(title: string, customSteps?: Partial<ProgressStep>[]): ProgressOperationConfig {
    const steps = [
        { ...STEP_DATA, ...customSteps?.[0] },
        { ...STEP_PDF, ...customSteps?.[1] },
        { ...STEP_RENDER, ...customSteps?.[2] }
    ];

    return { title, steps, category: 'report' };
}

/**
 * Crée une config d'export standard
 */
function buildExportConfig(title: string): ProgressOperationConfig {
    return {
        title,
        steps: [STEP_EXPORT_PREPARE, STEP_EXPORT_GENERATE, STEP_EXPORT_DOWNLOAD],
        category: 'export'
    };
}

/**
 * Crée une config d'export minimal (une seule étape, affichage simplifié)
 */
function buildMinimalExportConfig(title: string, estimatedDurationMs = 15000): ProgressOperationConfig {
    return {
        title,
        steps: [{
            id: 'export',
            label: 'Export en cours',
            weight: 100,
            estimatedDurationMs
        }],
        category: 'export',
        minimal: true
    };
}

// ============================================================================
// CONFIGURATIONS PAR TYPE D'OPÉRATION
// ============================================================================

export const PROGRESS_CONFIGS: Record<string, ProgressOperationConfig> = {

    // -------------------------------------------------------------------------
    // GRANDS LIVRES
    // -------------------------------------------------------------------------
    [CodesReports.GDL1]: buildReportConfig('Grand Livre Comptable'),
    [CodesReports.GDL2]: buildReportConfig('Grand Livre Tiers'),
    [CodesReports.GLVANA]: buildReportConfig('Grand Livre Analytique', [
        { estimatedDurationMs: 4000 },
        { estimatedDurationMs: 20000 },
        { estimatedDurationMs: 5000 }
    ]),

    // -------------------------------------------------------------------------
    // BALANCES
    // -------------------------------------------------------------------------
    [CodesReports.BAL1]: buildReportConfig('Balance Comptable', [
        { estimatedDurationMs: 2000 },
        { estimatedDurationMs: 8000 },
        { estimatedDurationMs: 3000 }
    ]),
    [CodesReports.BALANA]: buildReportConfig('Balance Analytique'),
    [CodesReports.BAL2]: buildReportConfig('Balance Tiers'),

    // -------------------------------------------------------------------------
    // EXPORTS
    // -------------------------------------------------------------------------
    'EXPORT_BENEFICIAIRES': buildExportConfig('Export des bénéficiaires'),
    'EXPORT_ECRITURES': buildExportConfig('Export des écritures'),

    // -------------------------------------------------------------------------
    // EXPORTS COMPTABILITÉ (mode minimal)
    // -------------------------------------------------------------------------
    'EXPORT_GRAND_LIVRE': buildMinimalExportConfig('Export Grand Livre'),
    'EXPORT_GRAND_LIVRE_TIERS': buildMinimalExportConfig('Export Grand Livre Tiers'),
    'EXPORT_GRAND_LIVRE_ANALYTIQUE': buildMinimalExportConfig('Export Grand Livre Analytique'),
    'EXPORT_BALANCE_COMPTABLE': buildMinimalExportConfig('Export Balance Comptable'),
    'EXPORT_BALANCE_TIERS': buildMinimalExportConfig('Export Balance Tiers'),
    'EXPORT_BALANCE_ANALYTIQUE': buildMinimalExportConfig('Export Balance Analytique'),

    // -------------------------------------------------------------------------
    // REQUÊTEUR
    // -------------------------------------------------------------------------
    [REQUETEUR_RECHERCHE]: buildMinimalExportConfig('Chargement des résultats', 8000),
    // Export = toutes les lignes → potentiellement long sur gros volume : durée estimée large.
    [REQUETEUR_EXPORT]: buildMinimalExportConfig('Génération de l\'export en cours', 25000),
    // Variante gros volume : même overlay + message d'attente affiché sous le titre.
    [REQUETEUR_EXPORT_LARGE]: {
        ...buildMinimalExportConfig('Génération de l\'export en cours', 40000),
        subtitle: 'Un grand nombre de lignes est traité : cet export peut prendre un peu de temps…'
    },

    // -------------------------------------------------------------------------
    // ATTRIBUTIONS
    // -------------------------------------------------------------------------
    // Le calcul des bénéficiaires d'une attribution peut durer plusieurs minutes sur un gros effectif :
    // les messages rassurent l'utilisateur pour qu'il ne quitte pas l'écran avant la fin.
    [ATTRIBUTION_PREPARATION]: {
        title: 'Préparation des bénéficiaires',
        subtitle: 'Merci de patienter sans quitter ni rouvrir cet écran.',
        steps: [{
            id: 'beneficiaires',
            label: 'Calcul des bénéficiaires',
            weight: 100,
            estimatedDurationMs: 60000,
            waitingMessages: [
                { afterMs: 0, message: 'Calcul des droits de chaque bénéficiaire…' },
                { afterMs: 30000, message: 'Sur un grand nombre de bénéficiaires, ce calcul prend plusieurs minutes.' },
                { afterMs: 90000, message: 'C\'est plus long que prévu, mais le calcul continue : la liste va s\'afficher.' }
            ]
        }],
        category: 'list',
        minimal: true
    },

    // -------------------------------------------------------------------------
    // CONFIG PAR DÉFAUT (fallback)
    // -------------------------------------------------------------------------
    'REPORT_DEFAULT': buildReportConfig('Génération du document'),
    'EXPORT_DEFAULT': buildExportConfig('Export en cours'),
};

/**
 * Récupère la config pour un type d'opération
 * Retourne la config par défaut si non trouvée
 */
export function getProgressConfig(operationType: string): ProgressOperationConfig {
    return PROGRESS_CONFIGS[operationType] ?? PROGRESS_CONFIGS['REPORT_DEFAULT'];
}
